import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import AutoDeleteOutlinedIcon from "@mui/icons-material/AutoDeleteOutlined";
import CheckIcon from "@mui/icons-material/Check";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useShallow } from "zustand/react/shallow";
import { Badge } from "components/common/Badge";
import { DropdownMenu } from "components/common/DropdownMenu";
import { Tooltip } from "components/common/Tooltip";
import { DropdownMenuItem } from "components/common/DropdownMenuItem";
import { TooltipButton } from "components/common/TooltipButton";
import { SidebarDropdown } from "@/components/main/utils/SidebarDropdown";
import { useSidebarFlyout } from "@/components/main/utils/useSidebarFlyout";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import { OrgMark } from "@/components/main/organizations/OrgMark";
import { formatDayMonthYear } from "@/lib/dates";
import { purgeDate } from "@/lib/deletionGrace";
import type { Org } from "@/lib/api/organizations";

/// Sidebar-top org context switcher (S6d). Lists the caller's orgs; picking
/// one re-scopes the overview and search (S6c narrowing).
///
/// The plan shows only in the dropdown's rows; the trigger names the org.
///
/// The hover-revealed gear is the entry point to org settings. It lives on
/// the trigger row, not in the dropdown — menus hold actions, not
/// destinations (DESIGN.md) — and every member gets it (D43).
const DoomMark: React.FC<{ org: Org }> = ({ org }) => (
  <Tooltip
    title={`Scheduled for deletion on ${
      org.purge_at
        ? formatDayMonthYear(org.purge_at)
        : purgeDate(org.deletion_scheduled_at ?? "")
    }\n${
      org.is_personal
        ? "It goes with your account; cancel from your account settings"
        : "Everything in it is in the Trash; an owner or admin can cancel"
    }`}
  >
    <AutoDeleteOutlinedIcon
      sx={{ fontSize: 13 }}
      className="shrink-0 text-warning"
      aria-label="Scheduled for deletion"
    />
  </Tooltip>
);

/// Folded with the rail it keeps the org's mark, which opens the rail's own
/// flyout as Spaces does: the org, its settings, and the orgs to switch to.
export const OrgSwitcher: React.FC<{
  folded?: boolean;
  sidebarWidth?: string;
}> = ({ folded = false, sidebarWidth }) => {
  // The whole row, not the trigger inside it — `DropdownMenu` sizes the menu
  // to its anchor, and the trigger is only part of the row's width.
  const anchorRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const { flyout, show, hideSoon, hide, onHoverChange } =
    useSidebarFlyout<"org">();
  useEffect(() => hide(), [folded, hide]);
  const { orgs, activeOrgId, switchOrg } = useOrgContextStore(
    useShallow((s) => ({
      orgs: s.orgs,
      activeOrgId: s.activeOrgId,
      switchOrg: s.switchOrg,
    })),
  );

  const active = orgs.find((o) => o.org_id === activeOrgId);

  // Org settings follows the switch, section and all; every other shell route
  // addresses something in the org just left and would keep rendering it.
  const destinationFor = (orgId: string) =>
    /^\/organizations\/[^/]+$/.test(location.pathname) &&
    location.pathname !== "/organizations/new"
      ? `/organizations/${orgId}${location.hash}`
      : "/overview";

  if (!active) return null;

  // One org is not a choice: no menu.
  const canSwitch = orgs.length > 1;

  // The mark keeps the rail icons' 32px slot, folded or not, and the name fades
  // as the rail labels do: the row never changes height, so nothing below moves.
  const fade = { opacity: folded ? 0 : 1, transition: "opacity 0.3s ease" };
  const identity = (
    <>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center">
        <OrgMark org={active} size="sm" />
      </span>
      <span className="min-w-0 truncate text-fg-semibold" style={fade}>
        {active.name}
      </span>
    </>
  );

  const settings = (
    <TooltipButton
      title="Settings"
      aria-label="Organization settings"
      variant="contextual"
      size="xs"
      onClick={(e) => {
        e.stopPropagation();
        setOpen(false);
        openSettings();
      }}
    >
      <SettingsOutlinedIcon fontSize="inherit" />
    </TooltipButton>
  );

  const openSettings = () => navigate(`/organizations/${active.org_id}`);
  const switchTo = (orgId: string) => {
    void switchOrg(orgId);
    navigate(destinationFor(orgId));
  };

  const flyoutPanel = folded && flyout && sidebarWidth && (
    <SidebarDropdown
      isVisible
      topPosition={flyout.top}
      sidebarWidth={sidebarWidth}
      maxWidth="var(--sidebar-width-open)"
      headerContent={
        <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
          <OrgMark org={active} size="sm" />
          <span className="min-w-0 flex-1 truncate text-fg-semibold">
            {active.name}
          </span>
          {active.deletion_scheduled_at && <DoomMark org={active} />}
        </div>
      }
      items={[
        {
          id: "settings",
          label: "Settings",
          icon: <SettingsOutlinedIcon className="icon-md" />,
          onClick: () => {
            hide();
            openSettings();
          },
        },
        ...(canSwitch
          ? [
              {
                id: "switch",
                label: "Switch organization",
                icon: <SwapHorizIcon className="icon-md" />,
                onClick: () => {},
                subItems: orgs.map((org) => ({
                  id: org.org_id,
                  label: org.name,
                  icon: <OrgMark org={org} size="sm" />,
                  selected: org.org_id === activeOrgId,
                  onClick: () => {
                    hide();
                    switchTo(org.org_id);
                  },
                })),
              },
            ]
          : []),
      ]}
      onHoverChange={onHoverChange}
    />
  );

  const menu = canSwitch && !folded && (
    <DropdownMenu
      anchorRef={anchorRef}
      isOpen={open}
      onClose={() => setOpen(false)}
      className="py-1"
    >
      <span className="block px-2 pt-1 pb-0.5 text-fg-muted-regular-small select-none">
        Member of
      </span>
      {orgs.map((org) => (
        // The space picker's look: the hover fill and a tick on the active org.
        <DropdownMenuItem
          key={org.org_id}
          className={org.org_id === activeOrgId ? "bg-list-hover" : ""}
          onClick={() => {
            setOpen(false);
            switchTo(org.org_id);
          }}
        >
          <span className="flex w-full min-w-0 items-center gap-2">
            <OrgMark org={org} size="sm" />
            <span className="min-w-0 flex-1 truncate text-left text-fg-regular">
              {org.name}
            </span>
            {org.org_id === activeOrgId && (
              <CheckIcon
                sx={{ fontSize: 14 }}
                className="shrink-0 text-accent"
              />
            )}
            {org.deletion_scheduled_at ? (
              <DoomMark org={org} />
            ) : (
              org.plan_name && <Badge label={org.plan_name} />
            )}
          </span>
        </DropdownMenuItem>
      ))}
    </DropdownMenu>
  );

  return (
    // The row, not the trigger, carries the tint — the gear and the deletion
    // mark sit outside the button (no nesting) but still light up with it.
    <div
      ref={anchorRef}
      className={`group flex w-full items-center gap-2 rounded-app py-0.5 ${
        folded ? "" : "pr-2"
      } ${open || flyout ? "bg-list-hover" : "hover:bg-list-hover"}`}
      onMouseEnter={folded ? (e) => show("org", e.currentTarget) : undefined}
      onMouseLeave={folded ? hideSoon : undefined}
    >
      {canSwitch || folded ? (
        <button
          onClick={(e) =>
            folded
              ? show("org", e.currentTarget.parentElement ?? e.currentTarget)
              : setOpen((v) => !v)
          }
          aria-label={canSwitch ? "Switch organization" : "Organization"}
          aria-expanded={folded ? !!flyout : open}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          {identity}
        </button>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-2">{identity}</div>
      )}
      {/* Folded, the rail is the mark's width; the gear is hover-only, so it never shows popping back. */}
      {!folded && (
        <span className="flex shrink-0 items-center opacity-0 transition-opacity group-hover:opacity-100 has-[:focus-visible]:opacity-100">
          {settings}
        </span>
      )}
      {!folded && active.deletion_scheduled_at && <DoomMark org={active} />}
      {menu}
      {flyoutPanel}
    </div>
  );
};
